import React, { useState, useEffect, useCallback } from "react";
import { Shield, Menu, X, ArrowRight, ChevronRight, Calendar, LogIn } from "lucide-react";

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Track window scroll to apply denser background glassmorphism
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 15);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is active
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  // Close mobile menu on Escape key press or desktop viewport resize
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth >= 860 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, [isMenuOpen]);

  const handleNavClick = useCallback(
    (e, targetId) => {
      setIsMenuOpen(false);
      if (window.location.pathname === "/") {
        e.preventDefault();
        const elem = document.getElementById(targetId);
        if (elem) {
          elem.scrollIntoView({ behavior: "smooth" });
          window.history.pushState(null, "", `/#${targetId}`);
        }
      }
    },
    []
  );

  const navLinks = [
    { id: "features", label: "Features", badge: "Threat Engine" },
    { id: "methodology", label: "Methodology", badge: "STRIDE & PASTA" },
    { id: "about", label: "About", badge: "Architecture" },
    { id: "pricing", label: "Pricing", badge: "Plans" },
  ];

  return (
    <>
      <header
        className={`tfl-nav ${isScrolled ? "tfl-nav--scrolled" : ""} ${
          isMenuOpen ? "tfl-nav--menu-open" : ""
        }`}
        aria-label="Global"
      >
        <nav className="tfl-nav__inner">
          {/* Brand Logo */}
          <a href="/" className="tfl-nav__brand" onClick={() => setIsMenuOpen(false)}>
            <div className="tfl-nav__brand-icon">
              <Shield size={18} color="#ffffff" />
            </div>
            <span className="tfl-nav__brand-text">ThreatForge</span>
          </a>

          {/* Desktop Navigation Links */}
          <ul className="tfl-nav__links" role="list">
            {navLinks.map((link) => (
              <li key={link.id}>
                <a href={`/#${link.id}`} onClick={(e) => handleNavClick(e, link.id)}>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Desktop Actions */}
          <div className="tfl-nav__actions">
            <a href="/login" className="tfl-nav__login">
              <span>Sign In</span>
            </a>
            <a href="/get-quote" className="tfl-btn tfl-btn--primary tfl-btn--nav">
              <span>Get Quote</span>
            </a>
            <a href="/book-demo" className="tfl-btn tfl-btn--ghost tfl-btn--nav">
              <span>Book a Demo</span>
            </a>
          </div>

          {/* Mobile / Small Screen Controls (Visible on <= 860px) */}
          <div className="tfl-nav__mobile-bar">
            <a href="/get-quote" className="tfl-btn tfl-btn--primary tfl-btn--mobile-cta">
              <span>Get Quote</span>
            </a>

            <button
              type="button"
              className={`tfl-nav__toggle ${isMenuOpen ? "tfl-nav__toggle--active" : ""}`}
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-label={isMenuOpen ? "Close menu" : "Open navigation menu"}
              aria-expanded={isMenuOpen}
              aria-controls="tfl-mobile-nav"
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Drawer Overlay - Rendered outside header to escape backdrop-filter containing block */}
      <div
        id="tfl-mobile-nav"
        className={`tfl-nav__drawer ${isMenuOpen ? "tfl-nav__drawer--open" : ""}`}
        aria-hidden={!isMenuOpen}
      >
        <div
          className="tfl-nav__drawer-backdrop"
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />

        <div className="tfl-nav__drawer-content">
          {/* Navigation Links Group */}
          <div className="tfl-nav__drawer-section">
            <div className="tfl-nav__drawer-heading">Platform</div>
            <ul className="tfl-nav__drawer-links" role="list">
              {navLinks.map((link) => (
                <li key={link.id}>
                  <a
                    href={`/#${link.id}`}
                    className="tfl-nav__drawer-link"
                    onClick={(e) => handleNavClick(e, link.id)}
                  >
                    <div className="tfl-nav__drawer-link-content">
                      <span className="tfl-nav__drawer-link-title">{link.label}</span>
                      <span className="tfl-nav__drawer-link-badge">{link.badge}</span>
                    </div>
                    <ChevronRight size={16} className="tfl-nav__drawer-link-arrow" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Drawer Actions */}
          <div className="tfl-nav__drawer-actions">
            <a
              href="/get-quote"
              className="tfl-btn tfl-btn--primary tfl-btn--drawer"
              onClick={() => setIsMenuOpen(false)}
            >
              <span>Get Quote</span>
              <ArrowRight size={16} />
            </a>

            <a
              href="/book-demo"
              className="tfl-btn tfl-btn--ghost tfl-btn--drawer"
              onClick={() => setIsMenuOpen(false)}
            >
              <Calendar size={16} />
              <span>Book a Demo</span>
            </a>

            <a
              href="/login"
              className="tfl-nav__drawer-login"
              onClick={() => setIsMenuOpen(false)}
            >
              <LogIn size={16} />
              <span>Sign In to Workspace</span>
            </a>
          </div>

          {/* Micro Status / Brand Footer in Drawer */}
          <div className="tfl-nav__drawer-footer">
            <span className="tfl-nav__drawer-status-dot" />
            <span>ThreatForge • Real-time Threat Intelligence</span>
          </div>
        </div>
      </div>
    </>
  );
}
